import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink } from 'lucide-react';
import CampCard from '../camps/CampCard';
import { Camp } from '../../utils/types';
import { useCampContext } from '../../context/CampContext';

interface CampSearchModalProps {
  camps: Camp[];
  selectedDates: Date[];
  selectedDatesByCampId: Record<string, string>;
  onDateChange: (campId: string, dateId: string) => void;
  getScheduledDateIds: (campId: string) => string[];
  onClose: () => void;
  onOpenInSearch: () => void;
}

const CampSearchModal: React.FC<CampSearchModalProps> = ({
  camps,
  selectedDates,
  selectedDatesByCampId,
  onDateChange,
  getScheduledDateIds,
  onClose,
  onOpenInSearch,
}) => {
  const { favorites } = useCampContext();

  // Sort camps: favorited first, then the rest
  const sortedCamps = useMemo(() => {
    const favoritedCampIds = new Set(favorites.map(fav => fav.id));

    return [...camps].sort((a, b) => {
      const aIsFavorited = favoritedCampIds.has(a.id);
      const bIsFavorited = favoritedCampIds.has(b.id);

      if (aIsFavorited && !bIsFavorited) return -1;
      if (!aIsFavorited && bIsFavorited) return 1;
      return 0; // Keep original order for camps in the same category
    });
  }, [camps, favorites]);

  return createPortal(
    <div className="camps-search-modal-overlay" onClick={onClose}>
      <div className="camps-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="camps-search-modal-header">
          <div className="camps-search-modal-header-content">
            <h2 className="camps-search-modal-title">
              {sortedCamps.length} {sortedCamps.length === 1 ? 'Camp' : 'Camps'} found.
            </h2>
            {selectedDates.length > 0 && (
              <p className="camps-search-modal-subtitle">
                {selectedDates.length === 1
                  ? selectedDates[0].toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                  : `${selectedDates.sort((a, b) => a.getTime() - b.getTime())[0].toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} – ${selectedDates.sort((a, b) => a.getTime() - b.getTime())[selectedDates.length - 1].toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`
                }
              </p>
            )}
          </div>
          <button
            className="camps-search-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="icon icon--sm" />
          </button>
        </div>

        <div className="camps-search-modal-content">
          {sortedCamps.length === 0 ? (
            <div className="camps-search-modal-empty">
              No camps found for these dates
            </div>
          ) : (
            <div className="camps-search-modal-grid">
              {sortedCamps.map((camp) => (
                <CampCard
                  key={camp.id}
                  camp={camp}
                  favoriteId="1"
                  scheduledDateIds={getScheduledDateIds(camp.id)}
                  selectedDate={selectedDatesByCampId[camp.id]}
                  onDateChange={onDateChange}
                />
              ))}
            </div>
          )}
        </div>

        <div className="camps-search-modal-footer">
          <button
            className="btn btn--secondary"
            onClick={onClose}
          >
            Close
          </button>
          <button
            className="btn btn--primary"
            onClick={onOpenInSearch}
          >
            <ExternalLink className="icon icon--xs" />
            Open in Search
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CampSearchModal;
